import React, { useState, useEffect, useCallback } from 'react';
import { API } from './App';

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  background: '#161616',
  border: '0.5px solid #2a2a2a',
  borderRadius: 8,
  color: '#f0ede8',
  fontSize: 13,
  fontFamily: "'DM Sans', sans-serif",
  boxSizing: 'border-box',
  outline: 'none'
};

const labelStyle = {
  display: 'block',
  fontSize: 11,
  color: '#666',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  marginBottom: 6
};

const modelColors = {
  traditional: { bg: '#1e1e2e', color: '#6b9fff' },
  surcharge: { bg: '#2a1e1a', color: '#fb923c' },
  dual: { bg: '#1a2a1e', color: '#4ade80' },
  buyerschoice: { bg: '#2a1a2a', color: '#c084fc' }
};

export default function Admin({ getToken }) {
  const [shops, setShops] = useState([]);
  const [shopsError, setShopsError] = useState(null);
  const [form, setForm] = useState({ shopName: '', contactEmail: '', contactPhone: '', processingModel: 'traditional' });
  const [addStatus, setAddStatus] = useState(null);

  // Account creation / reset state
  const [expandedShop, setExpandedShop] = useState(null);
  const [accountName, setAccountName] = useState('');
  const [accountBusy, setAccountBusy] = useState(false);
  const [accountResults, setAccountResults] = useState({}); // shopId → { emailSent, error }
  const [resetBusy, setResetBusy] = useState(null); // shopId being reset
  const [resetResults, setResetResults] = useState({}); // shopId → { sent, error }

  // Windows shop tool settings state
  const [toolShop, setToolShop] = useState(null); // shopId being edited
  const [toolForm, setToolForm] = useState({ processingModel: 'traditional', cardPricePercent: 3.5, settingsPassword: '' });
  const [toolBusy, setToolBusy] = useState(false);
  const [toolMsg, setToolMsg] = useState({}); // shopId → { ok } | { err }

  const loadShops = useCallback(() => {
    getToken()
      .then(token =>
        fetch(`${API}/api/shops`, { headers: { Authorization: `Bearer ${token}` } })
      )
      .then(r => {
        if (!r.ok) throw new Error(`Failed to load shops (${r.status})`);
        return r.json();
      })
      .then(data => { setShops(data); setShopsError(null); })
      .catch(err => setShopsError(err.message || 'Failed to load shops.'));
  }, [getToken]);

  useEffect(() => { loadShops(); }, [loadShops]);

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const handleAddShop = async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${API}/api/shops`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Request failed');
      }
      setAddStatus({ success: true, message: 'Shop added successfully!' });
      setForm({ shopName: '', contactEmail: '', contactPhone: '', processingModel: 'traditional' });
      loadShops();
    } catch (err) {
      setAddStatus({ success: false, message: err.message || 'Error adding shop.' });
    }
  };

  const openAccountForm = (shop) => {
    setExpandedShop(shop.ShopId);
    setAccountName(shop.ShopName);
    // Clear any previous result for this shop
    setAccountResults(prev => ({ ...prev, [shop.ShopId]: null }));
  };

  const handleCreateAccount = async (shop) => {
    setAccountBusy(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API}/api/admin/users`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: shop.ContactEmail, displayName: accountName || shop.ShopName }),
      });
      const data = await res.json();

      // 409 = account already existed in Auth0 (and is now tracked in DB)
      if (res.status === 409 && data.alreadyExists) {
        setAccountResults(prev => ({ ...prev, [shop.ShopId]: { emailSent: true, alreadyExisted: true } }));
        // Optimistically mark this shop as having an account in local state
        setShops(prev => prev.map(s =>
          s.ShopId === shop.ShopId ? { ...s, Auth0UserId: `exists:${shop.ContactEmail}` } : s
        ));
        setExpandedShop(null);
        loadShops();
        setAccountBusy(false);
        return;
      }

      if (!res.ok) throw new Error(data.error || 'Request failed');

      // Success — optimistically update local state so buttons change immediately
      setAccountResults(prev => ({ ...prev, [shop.ShopId]: { emailSent: true } }));
      setShops(prev => prev.map(s =>
        s.ShopId === shop.ShopId ? { ...s, Auth0UserId: data.userId } : s
      ));
      setExpandedShop(null);
      loadShops();
    } catch (err) {
      setAccountResults(prev => ({ ...prev, [shop.ShopId]: { error: err.message } }));
      setExpandedShop(null);
    }
    setAccountBusy(false);
  };

  const handleResetPassword = async (shop) => {
    setResetBusy(shop.ShopId);
    try {
      const token = await getToken();
      const res = await fetch(`${API}/api/admin/shops/${shop.ShopId}/reset-password`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Request failed');
      setResetResults(prev => ({ ...prev, [shop.ShopId]: { sent: true } }));
    } catch (err) {
      setResetResults(prev => ({ ...prev, [shop.ShopId]: { error: err.message } }));
    }
    setResetBusy(null);
    setTimeout(() => setResetResults(prev => ({ ...prev, [shop.ShopId]: null })), 5000);
  };

  const openToolSettings = (shop) => {
    if (toolShop === shop.ShopId) { setToolShop(null); return; }
    setToolShop(shop.ShopId);
    setToolForm({
      processingModel: shop.ProcessingModel || 'traditional',
      cardPricePercent: shop.CardPricePercent ?? 3.5,
      settingsPassword: shop.SettingsPassword || '',
    });
    setToolMsg(prev => ({ ...prev, [shop.ShopId]: null }));
  };

  const handleSaveToolSettings = async (shop) => {
    const pct = parseFloat(toolForm.cardPricePercent);
    if (isNaN(pct) || pct < 0 || pct > 10) {
      setToolMsg(prev => ({ ...prev, [shop.ShopId]: { err: 'Card price % must be between 0 and 10.' } }));
      return;
    }
    setToolBusy(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API}/api/shops/${shop.ShopId}/settings`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          processingModel: toolForm.processingModel,
          cardPricePercent: pct,
          settingsPassword: toolForm.settingsPassword,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Request failed');
      setToolMsg(prev => ({ ...prev, [shop.ShopId]: { ok: 'Saved. The shop tool applies this the next time it starts.' } }));
      loadShops();
    } catch (err) {
      setToolMsg(prev => ({ ...prev, [shop.ShopId]: { err: err.message } }));
    }
    setToolBusy(false);
  };

  const handleGenerateKey = async (shop) => {
    setToolBusy(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API}/api/shops/${shop.ShopId}/device-key`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Request failed');
      setToolMsg(prev => ({ ...prev, [shop.ShopId]: { ok: 'New device key generated — paste it into the shop tool config.' } }));
      loadShops();
    } catch (err) {
      setToolMsg(prev => ({ ...prev, [shop.ShopId]: { err: err.message } }));
    }
    setToolBusy(false);
  };

  return (
    <div>
      {/* ── Add New Shop ── */}
      <div style={{ fontSize: 11, color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 20 }}>Add New Shop</div>

      {addStatus && (
        <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 20, fontSize: 13, background: addStatus.success ? '#1a3a2a' : '#3a1a1a', color: addStatus.success ? '#4ade80' : '#f87171' }}>
          {addStatus.message}
        </div>
      )}

      <div style={{ background: '#161616', border: '0.5px solid #2a2a2a', borderRadius: 12, padding: 24, maxWidth: 480, display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 40 }}>
        {[
          { label: 'Shop Name', name: 'shopName', placeholder: "Bob's Auto" },
          { label: 'Contact Email', name: 'contactEmail', placeholder: 'owner@shop.com' },
          { label: 'Contact Phone', name: 'contactPhone', placeholder: '555-0100' },
        ].map(f => (
          <div key={f.name}>
            <label style={labelStyle}>{f.label}</label>
            <input name={f.name} value={form[f.name]} onChange={handleChange} placeholder={f.placeholder} style={inputStyle} />
          </div>
        ))}
        <div>
          <label style={labelStyle}>Processing Model</label>
          <select name="processingModel" value={form.processingModel} onChange={handleChange} style={{ ...inputStyle, cursor: 'pointer' }}>
            <option value="traditional">Traditional</option>
            <option value="surcharge">Surcharging</option>
            <option value="dual">Dual Pricing</option>
            <option value="buyerschoice">Buyer's Choice</option>
          </select>
        </div>
        <button onClick={handleAddShop}
          style={{ background: '#4ade80', color: '#0f0f0f', border: 'none', borderRadius: 8, padding: 12, fontSize: 13, fontWeight: 600, fontFamily: "'DM Sans', sans-serif", cursor: 'pointer', marginTop: 4 }}>
          Add Shop
        </button>
      </div>

      {/* ── All Shops ── */}
      <div style={{ fontSize: 11, color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12 }}>All Shops</div>

      {shopsError && (
        <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 12, fontSize: 13, background: '#3a1a1a', color: '#f87171' }}>
          {shopsError}
        </div>
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {['Shop Name', 'Email', 'Phone', 'Processing Model', 'Since', 'Login Account', 'Shop Tool'].map(h => (
              <th key={h} style={{ fontSize: 11, color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '8px 12px', textAlign: 'left', borderBottom: '0.5px solid #1e1e1e' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {shops.map(s => {
            const mc = modelColors[s.ProcessingModel] || modelColors.traditional;
            const result = accountResults[s.ShopId];
            const isExpanded = expandedShop === s.ShopId;

            return (
              <React.Fragment key={s.ShopId}>
                <tr style={{ borderBottom: isExpanded || result ? 'none' : '0.5px solid #1a1a1a' }}>
                  <td style={{ padding: 12, fontSize: 13, color: '#f0ede8', fontWeight: 500 }}>{s.ShopName}</td>
                  <td style={{ padding: 12, fontSize: 12, color: '#888' }}>{s.ContactEmail}</td>
                  <td style={{ padding: 12, fontSize: 12, color: '#888', fontFamily: "'DM Mono', monospace" }}>{s.ContactPhone}</td>
                  <td style={{ padding: 12 }}>
                    <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 4, fontFamily: "'DM Mono', monospace", background: mc.bg, color: mc.color }}>
                      {s.ProcessingModel}
                    </span>
                  </td>
                  <td style={{ padding: 12, fontSize: 12, color: '#555' }}>{new Date(s.CreatedAt).toLocaleDateString()}</td>
                  <td style={{ padding: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      {/* Create Account button — hidden once account exists */}
                      {!s.Auth0UserId && !result?.emailSent && !isExpanded && (
                        <button
                          onClick={() => openAccountForm(s)}
                          disabled={accountBusy}
                          style={{ background: '#1e1e2e', border: '0.5px solid #2a2a3a', color: '#6b9fff', fontSize: 11, padding: '4px 10px', borderRadius: 5, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}>
                          Create Account
                        </button>
                      )}

                      {/* Confirmed state — account exists (from DB or just created) */}
                      {(s.Auth0UserId || result?.emailSent) && !isExpanded && (
                        <button disabled style={{ background: '#1a3a2a', border: '0.5px solid #2a5a3a', color: '#4ade80', fontSize: 11, padding: '4px 10px', borderRadius: 5, cursor: 'default', fontFamily: "'DM Sans', sans-serif", opacity: 0.85 }}>
                          ✓ Account created
                        </button>
                      )}

                      {/* Reset Password button — only shown when account exists */}
                      {(s.Auth0UserId || result?.emailSent) && !isExpanded && (
                        resetResults[s.ShopId]?.sent
                          ? <span style={{ fontSize: 11, color: '#4ade80' }}>Reset sent</span>
                          : resetResults[s.ShopId]?.error
                            ? <span style={{ fontSize: 11, color: '#f87171' }}>{resetResults[s.ShopId].error}</span>
                            : <button
                                onClick={() => handleResetPassword(s)}
                                disabled={resetBusy === s.ShopId}
                                style={{ background: 'none', border: '0.5px solid #2a2a2a', color: '#888', fontSize: 11, padding: '4px 10px', borderRadius: 5, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}>
                                {resetBusy === s.ShopId ? 'Sending…' : 'Reset Password'}
                              </button>
                      )}

                      {result?.error && (
                        <span style={{ fontSize: 11, color: '#f87171' }}>{result.error}</span>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: 12 }}>
                    <button
                      onClick={() => openToolSettings(s)}
                      style={{ background: 'none', border: '0.5px solid #2a2a2a', color: '#888', fontSize: 11, padding: '4px 10px', borderRadius: 5, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}>
                      {toolShop === s.ShopId ? 'Close' : 'Tool Settings'}
                    </button>
                  </td>
                </tr>

                {/* Inline account creation form */}
                {isExpanded && (
                  <tr style={{ borderBottom: '0.5px solid #1a1a1a' }}>
                    <td colSpan={7} style={{ padding: '0 12px 16px' }}>
                      <div style={{ background: '#0d1a0d', border: '0.5px solid #1a3a1a', borderRadius: 8, padding: 16, display: 'flex', alignItems: 'flex-end', gap: 12 }}>
                        <div style={{ flex: 1 }}>
                          <label style={{ ...labelStyle, color: '#4ade80' }}>Sign-in email (from shop)</label>
                          <div style={{ fontSize: 12, color: '#888', padding: '10px 12px', background: '#111', borderRadius: 8, fontFamily: "'DM Mono', monospace" }}>
                            {s.ContactEmail}
                          </div>
                        </div>
                        <div style={{ flex: 1 }}>
                          <label style={{ ...labelStyle, color: '#4ade80' }}>Display name</label>
                          <input
                            value={accountName}
                            onChange={e => setAccountName(e.target.value)}
                            style={{ ...inputStyle, background: '#111', border: '0.5px solid #1a3a1a' }}
                          />
                        </div>
                        <div style={{ display: 'flex', gap: 8, paddingBottom: 1 }}>
                          <button
                            onClick={() => handleCreateAccount(s)}
                            disabled={accountBusy || !accountName}
                            style={{ background: '#4ade80', color: '#0f0f0f', border: 'none', borderRadius: 7, padding: '10px 16px', fontSize: 12, fontWeight: 600, fontFamily: "'DM Sans', sans-serif", cursor: 'pointer' }}>
                            {accountBusy ? 'Creating…' : 'Create'}
                          </button>
                          <button
                            onClick={() => setExpandedShop(null)}
                            style={{ background: 'none', border: '0.5px solid #2a2a2a', color: '#666', borderRadius: 7, padding: '10px 14px', fontSize: 12, fontFamily: "'DM Sans', sans-serif", cursor: 'pointer' }}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}

                {/* Confirmation banner */}
                {result?.emailSent && (
                  <tr style={{ borderBottom: '0.5px solid #1a1a1a' }}>
                    <td colSpan={7} style={{ padding: '0 12px 16px' }}>
                      <div style={{ background: '#0d1a0d', border: '0.5px solid #1a3a1a', borderRadius: 8, padding: 14, display: 'flex', alignItems: 'center', gap: 16 }}>
                        <div style={{ flex: 1 }}>
                          {result.alreadyExisted
                            ? <><div style={{ fontSize: 11, color: '#4ade80', marginBottom: 2 }}>✓ Existing account linked</div>
                                <div style={{ fontSize: 11, color: '#555' }}>Auth0 account for {s.ContactEmail} already existed and has been linked. Use Reset Password to send new credentials.</div></>
                            : <><div style={{ fontSize: 11, color: '#4ade80', marginBottom: 2 }}>✓ Account created</div>
                                <div style={{ fontSize: 11, color: '#555' }}>Welcome email sent to {s.ContactEmail} with login credentials and a password setup link.</div></>
                          }
                        </div>
                        <button
                          onClick={() => setAccountResults(prev => ({ ...prev, [s.ShopId]: null }))}
                          style={{ background: 'none', border: 'none', color: '#555', fontSize: 11, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}>
                          Dismiss
                        </button>
                      </div>
                    </td>
                  </tr>
                )}

                {/* Windows shop tool settings editor */}
                {toolShop === s.ShopId && (
                  <tr style={{ borderBottom: '0.5px solid #1a1a1a' }}>
                    <td colSpan={7} style={{ padding: '0 12px 16px' }}>
                      <div style={{ background: '#12151c', border: '0.5px solid #1e2733', borderRadius: 8, padding: 16, display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
                        <div style={{ width: 150 }}>
                          <label style={labelStyle}>Processing Model</label>
                          <select
                            value={toolForm.processingModel}
                            onChange={e => setToolForm({ ...toolForm, processingModel: e.target.value })}
                            style={{ ...inputStyle, cursor: 'pointer' }}>
                            <option value="traditional">Traditional</option>
                            <option value="surcharge">Surcharging</option>
                            <option value="dual">Dual Pricing</option>
                            <option value="buyerschoice">Buyer's Choice</option>
                          </select>
                        </div>
                        <div style={{ width: 100 }}>
                          <label style={labelStyle}>Card Price %</label>
                          <input
                            type="number" step="0.25" min="0" max="10"
                            value={toolForm.cardPricePercent}
                            onChange={e => setToolForm({ ...toolForm, cardPricePercent: e.target.value })}
                            style={inputStyle}
                          />
                        </div>
                        <div style={{ width: 150 }}>
                          <label style={labelStyle}>Settings Password</label>
                          <input
                            value={toolForm.settingsPassword}
                            onChange={e => setToolForm({ ...toolForm, settingsPassword: e.target.value })}
                            style={inputStyle}
                          />
                        </div>
                        <button
                          onClick={() => handleSaveToolSettings(s)}
                          disabled={toolBusy}
                          style={{ background: '#4ade80', color: '#0f0f0f', border: 'none', borderRadius: 7, padding: '10px 16px', fontSize: 12, fontWeight: 600, fontFamily: "'DM Sans', sans-serif", cursor: 'pointer' }}>
                          {toolBusy ? 'Saving…' : 'Save'}
                        </button>
                        <div style={{ flex: 1, minWidth: 260 }}>
                          <label style={labelStyle}>Device Key</label>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <div style={{ fontSize: 11, color: '#888', padding: '10px 12px', background: '#111', borderRadius: 8, fontFamily: "'DM Mono', monospace", flex: 1, overflowWrap: 'anywhere' }}>
                              {s.DeviceKey || '— none —'}
                            </div>
                            <button
                              onClick={() => handleGenerateKey(s)}
                              disabled={toolBusy}
                              style={{ background: '#1e1e2e', border: '0.5px solid #2a2a3a', color: '#6b9fff', fontSize: 11, padding: '4px 10px', borderRadius: 5, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}>
                              {s.DeviceKey ? 'Rotate' : 'Generate'}
                            </button>
                          </div>
                        </div>
                      </div>
                      {toolMsg[s.ShopId] && (
                        <div style={{ marginTop: 8, fontSize: 11, color: toolMsg[s.ShopId].err ? '#f87171' : '#4ade80' }}>
                          {toolMsg[s.ShopId].err || toolMsg[s.ShopId].ok}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
